import React, { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import type { ZohoStepDTO } from "../../data/zohoConnectApi";
import { runTallyPrepare } from "../../data/tallyPrepareApi";
import { useCoClient } from "../../data/store";
import { ConnectionSequence } from "../../components/ConnectionSequence";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

/**
 * Housekeeping in the open Tally company before Zournal works in it.
 *
 * Arriving looks, then fixes. The connector does the writes, so this screen
 * polls until the suspense bank is on the bank list and Sales and Purchase
 * numbering have been read back as Automatic (Manual Override). A failed
 * read-back keeps Continue closed: the CA sets numbering on the voucher type
 * screen in Tally.
 */
export function TallyPrepareStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "prepare-zoho");

  const [steps, setSteps] = useState<ZohoStepDTO[] | undefined>();
  const [refusal, setRefusal] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const locked = Boolean(client?.liveAt);

  useEffect(() => {
    if (locked) return;
    let live = true;

    setRefusal(null);
    runTallyPrepare(clientId, true)
      .then((state) => {
        if (live) setSteps(state.steps);
      })
      .catch((e: unknown) => {
        if (live) setRefusal(e instanceof Error ? e.message : "Could not reach the server.");
      });

    return () => {
      live = false;
    };
  }, [clientId, attempt, locked]);

  const busy = (steps ?? []).some((step) => step.status === "running" || step.status === "pending");

  useEffect(() => {
    if (locked || !busy) return;
    let live = true;
    const timer = window.setInterval(() => {
      runTallyPrepare(clientId, false)
        .then((state) => {
          if (live) setSteps(state.steps);
        })
        .catch((e: unknown) => {
          if (live) setRefusal(e instanceof Error ? e.message : "Could not reach the server.");
        });
    }, 2000);

    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [busy, clientId, locked]);

  if (!client) return null;

  const name = client.name || "this client";
  const anyFailed = (steps ?? []).some((step) => step.status === "failed");
  const complete = steps !== undefined && steps.length > 0 && steps.every((step) => step.status === "done");

  return (
    <SubStepShell
      title="Setting up Tally"
      description="We add the suspense bank and set Sales and Purchase numbering to Automatic (Manual Override) so Zournal can work in this company."
      thenWhat={complete ? "Continue → Assign Your Team." : "Continue opens once Tally is set up."}
      onContinue={() => void goNext()}
      continueDisabled={!complete}
    >
      {locked ? (
        <div className="rounded-lg border border-dashed px-5 py-4 text-[13px] text-muted-foreground">
          {name} is live — this housekeeping already ran, and re-running it would write to their books. Tally
          settings are managed in {name}'s own company from here on.
        </div>
      ) : steps ? (
        <ConnectionSequence
          title="Preparing Tally for Zournal"
          steps={steps}
          onRetry={anyFailed ? () => setAttempt((n) => n + 1) : undefined}
          showStatusWord
          showDoneDetail
        />
      ) : refusal ? (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed px-5 py-4">
          <p className="text-sm text-muted-foreground">{refusal}</p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="rounded-md border bg-background px-3 py-[5px] text-[13px] font-medium transition-colors hover:bg-muted/40"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed px-5 py-4 text-[13px] text-muted-foreground">
          Looking at {name}'s Tally company…
        </div>
      )}
    </SubStepShell>
  );
}
